import { CloseCircleOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Layout, Space } from "antd";
import { Content, Footer } from "antd/es/layout/layout";
import { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import { CrudServiceType } from '../../apis/ApiCrudService';
import { DataViewProps } from './DataView';
import { ToggleableProps } from "./Toggleable";
import { AxiosError } from 'axios';

type EditableInputProps<T> = {
    _save?: (data: T) => Promise<T>,
    _remove?: (data: T) => Promise<void>,

    onSaved?: (data: T, hideAfterSaving: boolean) => void,
    onDeleted?: (data: T) => void,

    error?: any,
    setError?: (error: any) => void
};

export type EditableProps<T> = DataViewProps<T> & {
    edit: (data: T | undefined) => void,
    cancel: () => void,
    saveAndStay: () => void,
    save: () => void,
    addOnSavedHandler: (handler: (data: T, hideAfterSaving: boolean) => void) => () => void,
    remove: () => void,
    error: any,
    setError: (error: any) => void
} & EditableInputProps<T>;


export const useEditable = function <T>(
    props: DataViewProps<T> & EditableInputProps<T>,
    toggle?: ToggleableProps
): EditableProps<T> {

    const show = () => { toggle && toggle.show(); }
    const hide = () => { toggle && toggle.hide(); }
    const setError = (error: any) => { props.setError && props.setError(error); }

    const { _save, _remove } = props;
    
    const _props = props as EditableProps<T>;

    
    const onSavedHandlers = useRef<Array<(data: T, hide: boolean) => void>>([]);
    const triggerOnSaved = (data: T, hideAfterSaving: boolean) => {
        props.onSaved?.(data, hideAfterSaving);
        onSavedHandlers.current.forEach(fn => fn(data, hideAfterSaving));
    };
    _props.addOnSavedHandler = (handler) => {
        onSavedHandlers.current.push(handler);
        return () => {
          onSavedHandlers.current = onSavedHandlers.current.filter(h => h !== handler);
        };
    };

    _props.edit = (data: T | undefined) => { 
        show(); 
        setError(undefined);
        props.setData(data);  
    }
    _props.cancel = () => { 
        props.setData(undefined);
        hide(); 
    }

    const __save = (hideAfterSaving: boolean) => {
        if (!props.data) throw new Error('data is undefined');
        let data = props.data;

        if (!_save) throw new Error('_save is undefined');
        
        _save(data).then(
            (savedData: T) => {
                setError(undefined);
                props.setData(savedData);
                if (hideAfterSaving) hide();  // cfillol: Is necessary?
                triggerOnSaved(savedData, hideAfterSaving);
            }
        ).catch((error: any) => {
            setError(error);
        });
    }

    _props.save = () => { __save(true); }

    _props.saveAndStay = () => { __save(false); }

    _props.remove = () => {
        if (!props.data) throw new Error('data is undefined');
        const data = props.data;
        if (!_remove) throw new Error('_remove is undefined');
        _remove(props.data).then(() => {
            setError(undefined);
            hide();
            props.onDeleted && props.onDeleted(data);
        }).catch((error: any) => {
            setError(error);
        });
    }

    return _props;
}


type PromiseCallbacks<T> = {
    resolve: (data: T) => void,
    reject: (error: any) => void,
} | undefined;

export const useEditableWithState = function <T>(
    props: DataViewProps<T>,
    toggle?: ToggleableProps
): EditableProps<T> {

    // Work with a copy of the data until save is done
    const [ data, setData ] = useState(props.data);
    const [ error, setError ] = useState<any>(undefined);

    let saveCallbacks: PromiseCallbacks<T>;
    // let deleteCallbacks: PromiseCallbacks<void>;

    return useEditable(
        { 
            data, setData, error, setError,
            _save: (data: T) => {
                props.setData(data);
                toggle && toggle.hide();
                return new Promise<T>((resolve, reject) => { saveCallbacks = { resolve, reject }; });
            },
            // _remove: () => {
            //     props.setData(null as T);
            //     return new Promise<any>((resolve, reject) => { deleteCallbacks = { resolve, reject }; });
            // },
        }, 
        toggle
    );
}

export const useApiEditable = function <T>(
    crudApiService: CrudServiceType<T, any>,
    props: DataViewProps<T> & EditableInputProps<T>,
    toggle?: ToggleableProps
): EditableProps<T> {

    // Work with a copy of the data until save is done
    const { data } = props;
    useEffect(() => {
        setServiceData(data && { ...data });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // const setError = useState()[1];
    let saveCallbacks: PromiseCallbacks<T>;
    let deleteCallbacks: PromiseCallbacks<void>;

    const { data: serviceData, setData: setServiceData, save, remove } = crudApiService({
        defaultData: props.data,
        onError: (axiosError: AxiosError) => {
            let error : any = axiosError;
            if (axiosError.response && axiosError.response.data) {
                error = axiosError.response.data;
            }

            saveCallbacks && saveCallbacks.reject(error);
            saveCallbacks = undefined;
        },
        onLoaded: (loadedData)=> { 
            // setError(undefined); 
            props.setData(loadedData);
        },
        onSaved: (updatedData: T) => {
            const _data = {...serviceData, ...updatedData}
            //props.setData(_data);
            // toggle && toggle.hide(); cfillol: Is necessary?
            saveCallbacks && saveCallbacks.resolve(_data);
            saveCallbacks = undefined;
        },
        onDeleted: () => { 
            //props.setData(undefined);
            // toggle && toggle.hide(); cfillol: Is necessary?
            deleteCallbacks && deleteCallbacks.resolve();
            deleteCallbacks = undefined;
        }
    });

    return useEditable({
        ...props,
        _save: (data: T) => {
            if (saveCallbacks) throw new Error('Waiting for previous save to finish');
            save();
            return new Promise<T>((resolve, reject) => { saveCallbacks = { resolve, reject }; });
        },
        _remove: () => {
            if (deleteCallbacks) throw new Error('Waiting for previous delete to finish');
            remove();
            return new Promise<any>((resolve, reject) => { deleteCallbacks = { resolve, reject }; });
        },
    }, toggle);

}

export const useStatefulApiEditable = function <T>(
    crudApiService: CrudServiceType<T, any>,
    data?: T | undefined,
    props?: EditableInputProps<T> | undefined,
    toggle?: ToggleableProps
): EditableProps<T> {

    const state = useState(data);
    const error = useState<any>(undefined);

    return useApiEditable(crudApiService, { 
        data: state[0], setData: state[1], 
        error: error[0], setError: error[1],
        ...props 
    }, toggle);

}

export const EditableWithButtonsLayout = function (props: {
    children: any,
    onSaveAndStayClick?: () => void,
    onSaveClick?: () => void,
    onDeleteClick?: () => void,
    onCancelClick?: () => void,
}) {

    const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
        if ((props.onSaveAndStayClick || props.onSaveClick) && (e.ctrlKey || e.metaKey) && e.key === "s") {
            e.preventDefault(); // Evita el comportamiento predeterminado del navegador
            if (props.onSaveAndStayClick) props.onSaveAndStayClick();
            else if (props.onSaveClick) props.onSaveClick();
        }
    }

    return (
        <Layout className="h-100" onKeyDown={onKeyDown}>
            <Content style={{ overflowY: "auto" }}>
                {props.children}
            </Content>
            <Footer style={{ paddingTop: "1.7em" }}>
                <Space>
                {props.onSaveAndStayClick && <Button icon={<SaveOutlined />} onClick={props.onSaveAndStayClick}>Save</Button>}
                {props.onSaveClick && <Button icon={<SaveOutlined />} onClick={props.onSaveClick}>{props.onSaveAndStayClick? 'Save and close' : 'Save'}</Button>}
                {props.onCancelClick && <Button icon={<CloseCircleOutlined />} onClick={props.onCancelClick}>Cancel</Button>}
                {props.onDeleteClick && <Button icon={<DeleteOutlined/>} onClick={props.onDeleteClick}>Delete</Button>}
                </Space>
            </Footer>
        </Layout>
    );

}

